import React from 'react';
import '../App.css';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useConfig } from '../context/ConfigContext';
import { useAuth } from '../context/AuthContext.jsx';
import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from 'axios';
import FavoriteButton from "../components/favoriteButton";
import LikeButton from "../components/likeButton";
import ReactButton from "../components/reactButton";
import MorphModal from '../components/morphModal.js';
import SketchModal from '../components/sketchModal.js';
import ReportModal from '../components/reportModal.js';


export const Activity = () => {
	const { backendApiBaseUrl, isTesting } = useConfig();
  	const { id, role, token } = useAuth();
	const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
	const [sidebarVisible, setSidebarVisible] = useState(false);
	const [items, setItems] = useState([]);
	const [searchParams, setSearchParams] = useSearchParams();
	const [sortOrder, setSortOrder] = useState('Newest');
    const [selectedItem, setSelectedItem] = useState(null);
	const [hasMore, setHasMore] = useState(true);
	const [pendingFilters, setPendingFilters] = useState({
		minFavorites: searchParams.get('minFavorites') || '',
		maxFavorites: searchParams.get('maxFavorites') || '',
		minLikeReact: searchParams.get('minLikeReact') || '',
		maxLikeReact: searchParams.get('maxLikeReact') || '',
		sort: searchParams.get('sort') || 'Newest',
	});
	const [appliedFilters, setAppliedFilters] = useState(() => {
        return Object.fromEntries(
            [...searchParams.entries()].filter(([key, value]) => value !== '')
        );
    });
	const [cursor, setCursor] = useState(null);
	const [openModal, setOpenModal] = useState(false);


	const fetchData = useCallback(async (cursorId = null, filters = {}) => {
		try {
			const url = new URL(`${backendApiBaseUrl}/activity`);

			if (cursorId) {
				url.searchParams.append("cursor", cursorId);
			}

			const cleanFilters = Object.fromEntries(
				Object.entries(filters).filter(([key, value]) => value !== undefined && value !== null && value !== "")
			);
			

			const response = await axios.post(url.toString(), {
				filters: cleanFilters,
				userId: id,
				cursor: cursorId
			});

			const { items: rawItems, hasMore: moreAvailable, cursor } = response.data;
			
			setCursor(cursor);
			setHasMore(moreAvailable);

			const extractContent = (item) =>
				item.morph || item.idol || item.album || item.sketch || item.frame;

			const normalizeItem = (item) => {
				const content = extractContent(item);
				if (!content) return null;
				const rawUsername = content?.username ?? content?.user?.username;
				const username = rawUsername?.replaceAll('"', '') ?? "Unknown";

				let images = content.images || [];

				return {
					eventId: item.id,
					rawItemId: content.id,
					id: `${item.targetType}-${content.id}`,
					type: item.targetType,
					card_code: content.cardCode,
					actorId: item.actorId,
					actorUsername: username,
					description: content.description,
					character: content.character,
					sketcher: content.sketcher,
					inkwell: content.inkwell,
					series: content.series,
					images,
					likeReactCount: content._count?.reactions || content._count?.likes || 0,
					favoriteCount: content._count?.favorites || 0,
					userReaction: (Array.isArray(content.reactions) && content.reactions.length > 0) ? content.reactions[0].type : null,
					hasLiked: Array.isArray(content.reactions || content.likes) && (content.reactions || content.likes).length > 0,
					hasFavorited: Array.isArray(content.favorites) && content.favorites.length > 0,
					prettyReactionsCount: content.prettyReactionsCount,
					fireReactionsCount: content.fireReactionsCount,
					uniqueReactionsCount: content.uniqueReactionsCount
				};
			}

			const transformed = Array.isArray(rawItems)
				? rawItems.map(normalizeItem).filter(Boolean)
				: [];

			if (cursorId) {
				setItems((prev) => [...prev, ...transformed]);
			} else {
				setItems(transformed);
			}
		} catch (err) {
			if (isTesting) {
				console.error("Failed to fetch activity", err);
			}
		}

	}, [backendApiBaseUrl, id, isTesting]);

	useEffect(() => {
		const sort = searchParams.get('sort') || 'Newest';
		const urlFilters = {
			minFavorites: searchParams.get("minFavorites") || '',
			maxFavorites: searchParams.get("maxFavorites") || '',
			minLikeReact: searchParams.get("minLikeReact") || '',
			maxLikeReact: searchParams.get("maxLikeReact") || '',
			sort: sort
		};

		setSortOrder(sort);
		setPendingFilters(urlFilters);
		fetchData(null, urlFilters);
		
		
		const handleResize = () => {
			setIsMobile(window.innerWidth < 768);
		};
	
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [searchParams, fetchData]);
	

	const handleFilterSidebarToggle = () => {
        setSidebarVisible(!sidebarVisible);
    };

    const handleOpenModal = (item) => {
        setSelectedItem(item);
    };

    const handleCloseModal = () => {
        setSelectedItem(null);
		setOpenModal(false);
    };

	

	const fetchMoreData = () => {
		if (!hasMore) {
			return;
		}
		if (cursor) {

			fetchData(cursor, appliedFilters);
		}
	};

	const handleSort = (value) => {
		setSortOrder(value);
		setPendingFilters((prev) => ({ ...prev, sort: value }));
		setSearchParams(prev => {
			const params = new URLSearchParams(prev);
			params.set("sort", value);
			return params;
		});
	};

	const handleFilterChange = (key, value) => {
		setPendingFilters(prev => ({ ...prev, [key]: value }));
	};

	const handleFilterSubmit = () => {
		const filtered = Object.fromEntries(
			Object.entries(pendingFilters).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
		);

		setSearchParams(new URLSearchParams(filtered).toString());
		setAppliedFilters(filtered)

		setItems([]);
		setHasMore(true);
		setCursor(null);
		fetchData(null, filtered);
	};

	const openReportModal = (item) => {
		setSelectedItem(item);
		setOpenModal(true);
	};

	const onSubmitReport = async(reportData) => {
		if (!token?.token) return;
		const reportUrl = new URL(`${backendApiBaseUrl}/reports`);
		try {
			await axios.post(reportUrl.toString(), 
					{ reportData },
				{
					headers: {
						"Authorization": `Bearer ${token.token}`,
					},
				}
			);

			setOpenModal(false);

		} catch (err) {
			if (isTesting) {
				console.error("Failed to fetch activity", err);
			}
		}
	};

	return (
		<div className='container-fluid m-0 p-0'>
			<div> 
				<div className='row'>
					<aside className={`col-12 col-md-3 sidebar ${sidebarVisible ? 'd-block' : 'd-none d-md-block'} p-3 rounded`}
						style={{ 
                            maxWidth: sidebarVisible ? '100%': '215px',
                            backgroundColor: "#2b2d31",
                            transition: 'max-width 0.3s ease',
                            overflow: 'hidden',
							marginTop: '8px'
                            }}
					>
						<h4>Filters</h4>
						<div className="mb-4">
							<label className="form-label filter-title">Favorites</label>
							<div className="d-flex gap-2">
								<input type="number" className="form-control form-control-sm" placeholder="Min" min="0" value={pendingFilters.minFavorites || ''}
								onChange={(e) => handleFilterChange("minFavorites", e.target.value)} />

								<input type="number"className="form-control form-control-sm" placeholder="Max" min="0" value={pendingFilters.maxFavorites || ''}
								onChange={(e) => handleFilterChange("maxFavorites", e.target.value)} />
							</div>
						</div>

						<div className="mb-4">
							<label className="form-label filter-title">Likes / Reactions</label>
							<div className="d-flex gap-2">
								<input type="number"  className="form-control form-control-sm" placeholder="Min" min="0" value={pendingFilters.minLikeReact || ''}
								onChange={(e) => handleFilterChange("minLikeReact", e.target.value)}/>
								
								<input type="number" className="form-control form-control-sm" placeholder="Max" min="0" value={pendingFilters.maxLikeReact || ''}
								onChange={(e) => handleFilterChange("maxLikeReact", e.target.value)}/>
							</div>
						</div>

						<button className="btn btn-primary mt-2" onClick={handleFilterSubmit}>
							Apply Filters
						</button>
					</aside>

					<div className='col-12 col-md-5' id="infiniteScrollContainer" style={{flexGrow: 1, padding: 0}}> 
						<div className="d-flex justify-content-between align-items-center px-4 rounded">
							<div className='p-2'>
								<button 
									className="btn btn-secondary " 
									onClick={handleFilterSidebarToggle}
									style={{
										display: (!isMobile && !sidebarVisible) ? 'none' : 'block',
										background: '#2b2d31',
									}}>
									<i className="fa-solid fa-filter"></i>
								</button>
							</div>

							<div className='d-flex justify-content-end align-items-center'>
								<div className="p-2 ">
									<select className="form-select" value={sortOrder} style={{color:"black"}} onChange={(e) => handleSort(e.target.value)}>
										<option className='profile-filter-dropdown' value="Newest">Newest</option>
										<option className='profile-filter-dropdown' value="Oldest">Oldest</option>
									</select>
								</div>
							</div>
						</div>

						{items.length === 0 ? (
                            <div className="text-center mt-4" style={{  color:"#FFF" }}>
                                No items found
                            </div>
                        ) : (
							<InfiniteScroll
								dataLength={items.length}
								next={fetchMoreData}
								hasMore={hasMore}
								loader={<h4>Loading...</h4>}
								scrollableTarget="infiniteScrollContainer"
								className="itemScrollableArea"
							>
								{items.map(item => (
									<div key={item.eventId} className="scroll-item">
										<div className="image-container">
											{id !== null && (
												<div className="d-flex justify-content-end pb-2">
													<div className='m-0 p-0' style={{ cursor: 'pointer' }} type="button" id='dropdownCardOptions' data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
														<i className="fa-solid fa-ellipsis-vertical"></i>
													</div>
													<div className="dropdown-menu" aria-labelledby="dropdownCardOptions">
														<div className='dropdown-item' onClick={() => openReportModal(item)} style={{ cursor: 'pointer' }}>Report</div>
													</div>
												</div>
											)}
											{item.images.length > 0 && (
												<img
													src={item.images[0].presignedUrl}
													alt="Item Preview"
													style={{ maxWidth: '100%', paddingBottom: '10px', cursor: 'pointer' }}
													onClick={() => handleOpenModal(item)}
												/>
											)}
											<div className="card-body">
												<h5 className="card-title">{item.actorUsername}</h5>
												<p>{item.type}</p>


												{role !== 'BAND ' && (
													<div style={{ display: 'flex', justifyContent: 'space-between' }}>
														{item?.type === 'MORPH' && (
															<>
																<LikeButton
																	itemType={item.type}
																	itemId={item.id}
																	userId={id}
																	hasLikedInitial={item.hasLiked}
																	likeCountInitial={item.likeReactCount}
																/>
																<FavoriteButton
																	itemType={item.type}
																	itemId={item.id}
																	userId={id}
																	hasFavoritedInitial={item.hasFavorited}
																	favoriteCountInitial={item.favoriteCount}
																/>
															</>
														)}
														{item?.type !== 'MORPH' && (
															<>
																<ReactButton
																	itemType={item.type}
																	itemId={item.id}
																	userId={id}
																	hasReactedInitial={item.userReaction}
																	reactCountInitial={item.likeReactCount}
																	uniqueReactionsCount={item.uniqueReactionsCount}
																	fireReactionsCount={item.fireReactionsCount}
																	prettyReactionsCount={item.prettyReactionsCount}
																/>

																<FavoriteButton
																	itemType={item.type}
																	itemId={item.id}
																	userId={id}
																	hasFavoritedInitial={item.hasFavorited}
																	favoriteCountInitial={item.favoriteCount}
																/>
															</>
														)}
													</div>
												)}
											</div>
										</div>
									</div>
								))}
							</InfiniteScroll>
						)}
                        {selectedItem?.type === 'MORPH' && openModal !== true && (
                            <MorphModal
                                selectedItem={selectedItem}
                                show={selectedItem !== null}
                                onClose={handleCloseModal}
                            />
                        )}
						{selectedItem?.type === 'SKETCH' && openModal !== true && (
                            <SketchModal
                                selectedItem={selectedItem}
                                show={selectedItem !== null}
                                onClose={handleCloseModal}
                            />
                        )}
						{openModal === true && (
                            <ReportModal
                                selectedItem={selectedItem}
								reporterId={id}
                                show={selectedItem !== null}
                                onClose={handleCloseModal}
								onSubmitReport={onSubmitReport}
                            />
                        )}

					</div>
				</div>
			</div>
		</div>
	);
};
